'use client';

import { useCommendaOSEntitySchema } from '@commenda/api/commenda-os-dsl';
import { MultiSelect } from '@commenda/kakashi';
import type { DataSourceConfig, ValidationRules } from '@commenda/types';
import { MONTHS } from '@commenda/utils/form-builder';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { DSLContextMarker, RESOLVED_LABEL_PREFIX } from '../../dsl-constants';
import { FormContext } from '../../hooks/FormContext';
import { isRuleActive } from '../../utils/validation';
import { AddressInputComponent } from './AddressInput';
import { fillAutoDetectFieldForPreview } from './autoDetectFieldPreviewFill';
import { DateInputComponent } from './DateInput';
import { EntitySelectComponent } from './EntitySelect';
import { FiscalYearInputComponent } from './FiscalYearInput';
import { InputComponent } from './Input';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';
import { NumberInputComponent } from './NumberInput';
import { RadioGroupComponent } from './RadioGroup';
import { SelectComponent } from './Select';
import { UrlInputComponent } from './UrlInput';

const ENTITY_ID_CONTEXT_KEY: Record<string, string> = {
  keyPerson: 'directorId',
  keyPersonRelationship: 'directorId',
};

interface AutoDetectFieldProps {
  fieldId: string;
  label?: string;
  placeholder?: string;
  guidance?: { text?: string };
  description?: string;
  validation?: {
    rules?: ValidationRules;
  };
  dataSource?: DataSourceConfig;
  props?: Record<string, unknown>;
}

/**
 * AutoDetectField Component
 *
 * A smart field that automatically determines the appropriate input type
 * based on the Commenda OS DSL schema. This allows form builders to quickly
 * add fields that are properly typed based on the entity schema.
 *
 * The component:
 * 1. Reads the entity and field path from the dataSource config
 * 2. Fetches the schema for that entity
 * 3. Determines the field type from the schema
 * 4. Renders the appropriate input component
 *
 * Context Bindings:
 * The component supports context bindings which allow it to use values from
 * previous fields (e.g., an EntitySelect) as its context. This is configured
 * via dataSource.commendaOS.contextBindings and is processed by the
 * useCommendaOSPrefill and useCommendaOSSubmit hooks at the form level.
 */
export const AutoDetectFieldComponent = ({
  fieldId,
  label,
  placeholder,
  guidance,
  description,
  validation,
  dataSource,
  props = {},
}: AutoDetectFieldProps) => {
  const {
    dslContext,
    formData,
    isFieldVisible,
    updateField,
    validateField,
    errors,
    isPreview,
    registerPreviewFillHandler,
  } = useContext(FormContext);

  // Extract entity and field from dataSource
  const entity = dataSource?.commendaOS?.entity;
  const fieldPath = dataSource?.commendaOS?.field?.read;
  const contextBindings = dataSource?.commendaOS?.contextBindings;

  const FORM_CONTEXT_MARKER = DSLContextMarker.FORM_CONTEXT;
  const ENTITY_CONTEXT_MARKER = DSLContextMarker.ENTITY_CONTEXT;

  // Build effective context by merging global context with bound field values
  // This is used for debugging and to check if required context is available
  const effectiveContext = useMemo(() => {
    const ctx = { ...dslContext };

    if (contextBindings) {
      for (const binding of contextBindings) {
        // If using form context marker, the value should already be in dslContext
        if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
          continue;
        }

        // If using entity context marker, the value is passed via props.__entityContext__
        if (binding.sourceFieldId === ENTITY_CONTEXT_MARKER) {
          const entityContext = (props as Record<string, unknown>)
            ?.__entityContext__ as Record<string, unknown> | undefined;
          if (entityContext?.dslContext) {
            const innerContext = entityContext.dslContext as Record<
              string,
              unknown
            >;
            if (innerContext[binding.contextKey] !== undefined) {
              ctx[binding.contextKey] = innerContext[binding.contextKey] as
                | string
                | number;
            }
          }
          continue;
        }

        const fieldValue = formData[binding.sourceFieldId];
        if (fieldValue !== undefined) {
          // Convert string values to numbers for ID fields
          ctx[binding.contextKey] =
            typeof fieldValue === 'string' && !isNaN(Number(fieldValue))
              ? Number(fieldValue)
              : (fieldValue as string | number);
        }
      }
    }

    return ctx;
  }, [dslContext, formData, contextBindings, props]);

  // Check if this is a new entity context (from EntitySelectOrCreate or BusinessEntitySelectOrCreate)
  // Must be defined before hasMissingBoundContext since it depends on this
  const isNewEntityContext = useMemo(() => {
    const entityContext = (props as Record<string, unknown>)
      ?.__entityContext__ as Record<string, unknown> | undefined;
    return entityContext?.isNewEntity === true;
  }, [props]);

  // Check if we have context bindings but the bound field doesn't have a value yet
  const hasMissingBoundContext = useMemo(() => {
    // For new entities, context bindings are cleared - field should be enabled
    if (isNewEntityContext) return false;

    if (!contextBindings || contextBindings.length === 0) return false;

    return contextBindings.some((binding) => {
      const key = binding.contextKey;

      if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
        const value = dslContext?.[key];
        const isMissing = value === undefined || value === null;
        if (
          (entity === 'keyPerson' || entity === 'keyPersonRelationship') &&
          key === 'directorId' &&
          isMissing &&
          (dslContext?.companyId != null || dslContext?.corporationId != null)
        ) {
          return false;
        }
        return isMissing;
      }

      // If using entity context marker, check if the entity context has the value
      if (binding.sourceFieldId === ENTITY_CONTEXT_MARKER) {
        const entityContext = (props as Record<string, unknown>)
          ?.__entityContext__ as Record<string, unknown> | undefined;
        if (entityContext?.dslContext) {
          const innerContext = entityContext.dslContext as Record<
            string,
            unknown
          >;
          const innerValue = innerContext[key];
          return innerValue === undefined || innerValue === null;
        }
        // If no entity context provided, context is missing
        return true;
      }

      const fieldValue = formData[binding.sourceFieldId];
      return (
        fieldValue === undefined || fieldValue === null || fieldValue === ''
      );
    });
  }, [
    entity,
    contextBindings,
    formData,
    dslContext,
    props,
    isNewEntityContext,
  ]);

  // Fetch entity schema
  const { entitySchema, isLoading } = useCommendaOSEntitySchema(entity);

  // Check if we're in update mode (have an entity ID in context)
  // This determines whether we check for update path or create path
  // Use effectiveContext which includes bound field values (e.g., from EntitySelect)
  // NOTE: This hook must be called before any early returns to maintain hook order
  const isUpdateContext = useMemo(() => {
    if (isNewEntityContext) return false;

    const schemaIdKey = (
      entitySchema as unknown as Record<string, unknown> | null
    )?.idContextKey as string | undefined;
    const entityIdKey =
      schemaIdKey ||
      (entity ? (ENTITY_ID_CONTEXT_KEY[entity] ?? `${entity}Id`) : null);
    return entityIdKey ? !!effectiveContext?.[entityIdKey] : false;
  }, [entity, effectiveContext, isNewEntityContext, entitySchema]);

  // Determine field type from schema by searching by field key or read.path
  const fieldInfo = useMemo(() => {
    if (!entitySchema || !fieldPath) return null;

    const findFieldByKeyOrReadPath = (
      fields: Record<string, unknown>,
      targetPath: string,
      parentPath = ''
    ): {
      type: string;
      parsingType?: string;
      label?: string;
      multiple?: boolean;
      enumOptions?: Array<{ value: string; label: string }>;
    } | null => {
      for (const [key, field] of Object.entries(fields)) {
        const f = field as Record<string, unknown>;
        const currentPath = parentPath ? `${parentPath}.${key}` : key;
        const readConfig = f.read as { path?: string } | undefined;

        if (currentPath === targetPath || readConfig?.path === targetPath) {
          return {
            type: f.type as string,
            parsingType: f.parsingType as string | undefined,
            label: label || (f.label as string),
            multiple: f.multiple as boolean | undefined,
            enumOptions: f.enumOptions as
              | Array<{ value: string; label: string }>
              | undefined,
          };
        }

        if (f.type === 'object' && f.fields) {
          const found = findFieldByKeyOrReadPath(
            f.fields as Record<string, unknown>,
            targetPath,
            currentPath
          );
          if (found) return found;
        }
      }
      return null;
    };

    return findFieldByKeyOrReadPath(
      entitySchema.fields as unknown as Record<string, unknown>,
      fieldPath
    );
  }, [entitySchema, fieldPath, label]);

  // Store resolved display label alongside raw value for summary view rendering.
  // This runs when the field value or schema info changes, persisting a
  // human-readable label (e.g., "Cash" instead of "CASH") under a __label__ key.
  const currentValue = formData[fieldId];
  const prevLabelRef = useRef<string | undefined>();

  useEffect(() => {
    if (currentValue == null || currentValue === '' || !fieldInfo) return;

    let resolvedLabel: string | null = null;

    if (fieldInfo.type === 'enum' && fieldInfo.enumOptions) {
      const option = fieldInfo.enumOptions.find(
        (opt) => String(opt.value) === String(currentValue)
      );
      if (option?.label) resolvedLabel = option.label;
    } else if (fieldInfo.type === 'boolean') {
      resolvedLabel = String(currentValue) === 'true' ? 'Yes' : 'No';
    } else if (fieldInfo.parsingType === 'fiscalYear') {
      if (typeof currentValue === 'string') {
        // Fiscal year stored as "MM-DD" → formatted as "Month Day" (e.g., "December 31")
        const match = /^(\d{1,2})-(\d{1,2})$/.exec(currentValue);
        if (match) {
          const monthIndex = parseInt(match[1], 10) - 1;
          const day = parseInt(match[2], 10);
          const monthLabel = MONTHS[monthIndex]?.label;
          if (monthLabel) {
            resolvedLabel = `${monthLabel} ${day}`;
          }
        }
      } else if (typeof currentValue === 'object' && currentValue !== null) {
        // Pre-filled DSL object: { type: "FIXED_DATE", data: { FIXED_DATE: { month: "DEC", day: 31 } } }
        const fiscalYearObj = currentValue as {
          data?: { FIXED_DATE?: { month?: string; day?: number } };
        };
        const fixedDate = fiscalYearObj.data?.FIXED_DATE;
        if (fixedDate?.month && fixedDate?.day) {
          const monthEntry = MONTHS.find((m) =>
            m.label.toUpperCase().startsWith(fixedDate.month!)
          );
          if (monthEntry) {
            resolvedLabel = `${monthEntry.label} ${fixedDate.day}`;
          }
        }
      }
    } else if (
      (fieldInfo.parsingType === 'date' || fieldInfo.type === 'date') &&
      typeof currentValue === 'string'
    ) {
      // Date stored as ISO "YYYY-MM-DD" → formatted as "Month Day, Year" (e.g., "December 31, 2024")
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(currentValue);
      if (match) {
        const monthIndex = parseInt(match[2], 10) - 1;
        const day = parseInt(match[3], 10);
        const monthLabel = MONTHS[monthIndex]?.label;
        if (monthLabel) {
          resolvedLabel = `${monthLabel} ${day}, ${match[1]}`;
        }
      }
    }

    if (resolvedLabel && resolvedLabel !== prevLabelRef.current) {
      prevLabelRef.current = resolvedLabel;
      updateField(`${RESOLVED_LABEL_PREFIX}${fieldId}`, resolvedLabel);
    }
  }, [currentValue, fieldInfo, fieldId, updateField]);

  // Check if schema says field is read-only based on the current context
  // - In UPDATE context: field is read-only if it has no update path
  // - In CREATE context: field is read-only if it has no create path
  // This acts as a safety net - even if form builder says 'read-write', schema wins
  // NOTE: This hook must be called before any early returns to maintain hook order
  const schemaIsReadOnly = useMemo(() => {
    if (!entitySchema || !fieldPath) return false;

    const findFieldWritability = (
      fields: Record<string, unknown>,
      targetPath: string,
      parentPath = ''
    ): boolean | null => {
      for (const [key, field] of Object.entries(fields)) {
        const f = field as Record<string, unknown>;
        const currentPath = parentPath ? `${parentPath}.${key}` : key;
        const readConfig = f.read as { path?: string } | undefined;

        if (currentPath === targetPath || readConfig?.path === targetPath) {
          const hasUpdate = !!(f.update as { path?: string })?.path;
          const hasCreate = !!(f.create as { path?: string })?.path;
          if (isNewEntityContext) return !hasCreate && !hasUpdate;
          return isUpdateContext ? !hasUpdate : !hasCreate;
        }

        if (f.type === 'object' && f.fields) {
          const found = findFieldWritability(
            f.fields as Record<string, unknown>,
            targetPath,
            currentPath
          );
          if (found !== null) return found;
        }
      }
      return null;
    };

    return (
      findFieldWritability(
        entitySchema.fields as Record<string, unknown>,
        fieldPath
      ) ?? false
    );
  }, [entitySchema, fieldPath, isUpdateContext, isNewEntityContext]);

  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  // Disable the field if context binding is missing (user needs to select in previous field first)
  const isDisabledDueToMissingContext =
    hasMissingBoundContext && contextBindings && contextBindings.length > 0;

  // Field is read-only if:
  // 1. Form builder explicitly set it to 'read' mode (user choice honored), OR
  // 2. Schema says it's read-only (even if form builder says 'read-write' - schema wins)
  const isReadOnlyMode =
    dataSource?.commendaOS?.mode === 'read' || schemaIsReadOnly;

  useEffect(() => {
    if (!isPreview || !registerPreviewFillHandler) return undefined;
    registerPreviewFillHandler(fieldId, () =>
      fillAutoDetectFieldForPreview({
        fieldId,
        currentValue,
        isWaitingOnContext: Boolean(isDisabledDueToMissingContext),
        isLoading,
        isReadOnly: isReadOnlyMode,
        fieldInfo,
        rules: validation?.rules,
        updateField,
        validateField,
      })
    );
    return () => registerPreviewFillHandler(fieldId, null);
  }, [
    isPreview,
    registerPreviewFillHandler,
    fieldId,
    currentValue,
    isDisabledDueToMissingContext,
    isLoading,
    isReadOnlyMode,
    fieldInfo,
    updateField,
    validateField,
    validation?.rules,
  ]);

  // Show placeholder while loading or if not configured
  if (isLoading || !fieldInfo) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(label || description) && (
          <LabelWithGuidanceTooltip
            label={label || 'Auto Field'}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={description}
          />
        )}
        <div className="p-4 border border-dashed rounded-md bg-bg-secondary">
          <p className="text-sm text-text-tertiary text-center">
            {!entity || !fieldPath
              ? 'Configure data source to auto-detect field type'
              : isLoading
                ? 'Loading data...'
                : 'Unable to determine field type'}
          </p>
        </div>
      </div>
    );
  }

  // Map schema type to component
  const effectiveLabel = label || fieldInfo.label;

  const commonProps = {
    fieldId,
    label: effectiveLabel,
    placeholder: isDisabledDueToMissingContext
      ? 'Select an entity above first...'
      : isReadOnlyMode
        ? 'This field is read-only'
        : placeholder,
    guidance,
    description,
    validation,
    props: {
      ...props,
      // Merge disabled state - field is disabled if props say so, context is missing, or mode is read
      disabled:
        (props.disabled as boolean) ||
        isDisabledDueToMissingContext ||
        isReadOnlyMode,
    },
  };

  // First check parsingType for specialized rendering
  if (fieldInfo.parsingType) {
    switch (fieldInfo.parsingType) {
      case 'date':
        return <DateInputComponent {...commonProps} />;
      case 'url':
        return <UrlInputComponent {...commonProps} />;
      case 'fiscalYear':
        return <FiscalYearInputComponent {...commonProps} />;
      case 'corporationReference':
        return (
          <EntitySelectComponent
            {...commonProps}
            props={{
              ...commonProps.props,
              entity: 'corporation',
              displayField: 'generalCorporationInfo.legalName',
              valueField: 'id',
              listPath: 'company.corporations',
            }}
          />
        );
    }
  }

  // keyPerson locations with locationType filter: render AddressInput (value shape: { locationType, address })
  const locationTypeFilter = dataSource?.commendaOS?.filters?.find(
    (f) => f.key === 'locationType'
  );
  if (
    fieldInfo.type === 'object' &&
    entity === 'keyPerson' &&
    fieldPath === 'locations' &&
    locationTypeFilter?.value
  ) {
    return (
      <AddressInputComponent
        {...commonProps}
        props={{
          ...commonProps.props,
          locationType: locationTypeFilter.value,
          defaultCountry: (props.defaultCountry as string) || 'US',
        }}
      />
    );
  }

  // Fall back to type-based rendering
  switch (fieldInfo.type) {
    case 'number':
      return <NumberInputComponent {...commonProps} />;

    case 'boolean':
      return (
        <RadioGroupComponent
          {...commonProps}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          props={{ ...commonProps.props, radioLayout: 'horizontal' }}
        />
      );

    case 'enum':
      // Multi-select when schema has multiple: true (e.g. boardPosition on keyPersonRelationship)
      if (fieldInfo.multiple === true) {
        const options = fieldInfo.enumOptions || [];
        const rawValue = formData[fieldId];
        const multiValue: string[] = Array.isArray(rawValue)
          ? rawValue
          : rawValue != null && rawValue !== ''
            ? [String(rawValue)]
            : [];
        return (
          <div className="mb-4" data-field-id={fieldId}>
            {(effectiveLabel || description) && (
              <LabelWithGuidanceTooltip
                label={effectiveLabel}
                guidance={commonProps.guidance?.text}
                isRequired={isRequired}
                description={description}
              />
            )}
            <MultiSelect
              id={`${fieldId}-multiselect`}
              options={options}
              defaultValue={multiValue}
              onValueChange={(newValue) => {
                updateField(fieldId, newValue);
                validateField(fieldId, newValue, validation?.rules);
              }}
              placeholder={commonProps.placeholder || 'Select options...'}
              error={errors[fieldId]}
              disabled={commonProps.props?.disabled as boolean}
              hideSelectAll
            />
            {errors[fieldId] && (
              <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
            )}
          </div>
        );
      }
      // Single select
      return (
        <SelectComponent
          {...commonProps}
          options={
            fieldInfo.enumOptions || [
              { value: '', label: 'No options available' },
            ]
          }
        />
      );

    case 'date':
      return <DateInputComponent {...commonProps} />;

    case 'string':
    default:
      return <InputComponent {...commonProps} />;
  }
};
